import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Copy } from "lucide-react";
import { useMemo } from "react";
import { useState } from "react";

type PayloadViewerProps = {
  payload: unknown;
  className?: string;
};

function parsePayload(payload: unknown) {
  if (payload == null) return null;

  // If already object/array
  if (typeof payload === "object") {
    return payload;
  }

  // If string, try parse JSON
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return payload;
    }
  }

  return payload;
}

export const PayloadViewer = ({ payload, className }: PayloadViewerProps) => {
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(() => parsePayload(payload), [payload]);

  const formatted = useMemo(() => {
    try {
      return typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2);
    } catch {
      return String(parsed);
    }
  }, [parsed]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formatted);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={className}>
      <div className="rounded-lg border bg-muted/30">
        <ScrollArea className="relative max-h-[500px] w-full">
          <pre className="p-1 text-xs leading-5 overflow-x-auto">
            <code>{formatted}</code>
          </pre>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="absolute cursor-pointer top-0.5 right-0 h-6 px-1"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </Button>
        </ScrollArea>
      </div>
    </div>
  );
};
